import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import type { AudioProcessor } from "@twilio/voice-sdk";
import { MEETINGS_AI_SOCKET_BASE_URL } from "@/config";
import {
    startSentimentAnalysisSession,
    stopSentimentAnalysisSession,
} from "@/services/meetings-ai/ai-service";
import { float32ToMulaw8k, resampleTo, uint8ToBase64 } from "@/utils/audio-utils";
import {
    TSentimentEvent,
    TSentimentHighlightAndSuggestion,
    TSentimentPainPoint,
    TSentimentStats,
} from "@/models/meetings-ai/sentiment.analysis.model";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { useTwilioContext } from "./twilio.context";

/* =======================
   Audio Tap Processor
======================= */

const TARGET_SAMPLES = 8000 * 0.25; // 250ms at 8kHz (mulaw)

class TwilioAudioTapProcessor implements AudioProcessor {
    private audioContext: AudioContext | null = null;
    private scriptProcessor: ScriptProcessorNode | null = null;
    private source: MediaStreamAudioSourceNode | null = null;
    private silentActivator: GainNode | null = null;
    private sampleBuffer = new Float32Array(0);

    constructor(
        private readonly ws: WebSocket,
        private readonly callStartTime: number,
    ) {}

    async createProcessedStream(stream: MediaStream): Promise<MediaStream> {
        this.audioContext = new AudioContext();
        // AudioContext is auto-suspended when created outside a direct user gesture.
        // Resume it immediately so ScriptProcessorNode fires and audio passes through.
        if (this.audioContext.state === "suspended") {
            await this.audioContext.resume();
        }
        this.source = this.audioContext.createMediaStreamSource(stream);

        // 4096 samples ≈ 85ms at 48kHz — we accumulate to 250ms before sending
        this.scriptProcessor = this.audioContext.createScriptProcessor(4096, 1, 1);

        this.scriptProcessor.onaudioprocess = (event) => {
            const inputData = event.inputBuffer.getChannelData(0);

            if (this.ws.readyState !== WebSocket.OPEN) return;

            // Resample to 8kHz for mulaw encoding
            const resampled = resampleTo(
                new Float32Array(inputData),
                this.audioContext!.sampleRate,
                8000,
            );

            const merged = new Float32Array(this.sampleBuffer.length + resampled.length);
            merged.set(this.sampleBuffer);
            merged.set(resampled, this.sampleBuffer.length);
            this.sampleBuffer = merged;

            while (this.sampleBuffer.length >= TARGET_SAMPLES) {
                const chunk = this.sampleBuffer.slice(0, TARGET_SAMPLES);
                this.sampleBuffer = this.sampleBuffer.slice(TARGET_SAMPLES);

                const mulaw = float32ToMulaw8k(chunk, 8000);
                const base64 = uint8ToBase64(mulaw);
                const timestampMs = Math.floor(performance.now() - this.callStartTime);

                this.ws.send(JSON.stringify({ type: "audio", data: base64, timestamp_ms: timestampMs }));
            }
        };

        // source → scriptProcessor → silentActivator (gain=0) → audioContext.destination
        // The gain=0 node drives the Web Audio pull chain without playing audio twice.
        this.silentActivator = this.audioContext.createGain();
        this.silentActivator.gain.value = 0;
        this.source.connect(this.scriptProcessor);
        this.scriptProcessor.connect(this.silentActivator);
        this.silentActivator.connect(this.audioContext.destination);

        // Return the original stream so Twilio plays it natively — lead voice works.
        return stream;
    }

    async destroyProcessedStream(): Promise<void> {
        this.scriptProcessor?.disconnect();
        this.source?.disconnect();
        this.silentActivator?.disconnect();
        await this.audioContext?.close();
        this.audioContext = null;
        this.scriptProcessor = null;
        this.source = null;
        this.silentActivator = null;
        this.sampleBuffer = new Float32Array(0);
    }
}

/* =======================
   Context
======================= */

export type TVoiceTranscriptEntry = {
    id?: number;
    speaker: string;
    text: string;
    timestamp?: string;
    isFinal: boolean;
};

type VoiceSentimentContextProps = {
    isSessionActive: boolean;
    connectionStatus: { audioAgent: boolean; audioLead: boolean; client: boolean };
    stats: TSentimentStats | null;
    painPoints: TSentimentPainPoint[];
    positiveHighlights: TSentimentHighlightAndSuggestion[];
    negativeHighlights: TSentimentHighlightAndSuggestion[];
    transcripts: TVoiceTranscriptEntry[];
};

const VoiceSentimentContext = createContext<VoiceSentimentContextProps>({
    isSessionActive: false,
    connectionStatus: { audioAgent: false, audioLead: false, client: false },
    stats: null,
    painPoints: [],
    positiveHighlights: [],
    negativeHighlights: [],
    transcripts: [],
});

/* =======================
   Provider
======================= */

export function VoiceSentimentProvider({ children }: PropsWithChildren) {
    const { state, sessionName, leadName, addAudioProcessor, removeAudioProcessor } = useTwilioContext();
    const authUser = useSelector((s: RootState) => s.auth.user);
    const agentName = authUser ? `${authUser.firstName} ${authUser.lastName}`.trim() : "agent";

    // Refs so startSession always reads the latest names without needing them in its deps array
    const agentNameRef = useRef(agentName);
    agentNameRef.current = agentName;
    const leadNameRef = useRef(leadName);
    leadNameRef.current = leadName;

    const wsAgentRef = useRef<WebSocket | null>(null);
    const wsLeadRef = useRef<WebSocket | null>(null);
    const wsClientRef = useRef<WebSocket | null>(null);
    const agentProcessorRef = useRef<TwilioAudioTapProcessor | null>(null);
    const leadProcessorRef = useRef<TwilioAudioTapProcessor | null>(null);
    const sessionIdRef = useRef<string | null>(null);
    const isStartingRef = useRef(false);

    const [isSessionActive, setIsSessionActive] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState({
        audioAgent: false,
        audioLead: false,
        client: false,
    });
    const [stats, setStats] = useState<TSentimentStats | null>(null);
    const [painPoints, setPainPoints] = useState<TSentimentPainPoint[]>([]);
    const [positiveHighlights, setPositiveHighlights] = useState<TSentimentHighlightAndSuggestion[]>([]);
    const [negativeHighlights, setNegativeHighlights] = useState<TSentimentHighlightAndSuggestion[]>([]);
    const [transcripts, setTranscripts] = useState<TVoiceTranscriptEntry[]>([]);

    const openWebSocket = (url: string, timeout = 5000): Promise<WebSocket> =>
        new Promise((resolve, reject) => {
            const ws = new WebSocket(url);
            const id = setTimeout(() => { ws.close(); reject(new Error(`WS timeout: ${url}`)); }, timeout);
            ws.onopen = () => { clearTimeout(id); resolve(ws); };
            ws.onerror = () => { clearTimeout(id); reject(new Error(`WS error: ${url}`)); };
        });

    // Reset the collected insights/transcripts. Called when a NEW call starts,
    // NOT when a call ends — so the last call's insights stay visible on screen
    // until the next conversation begins.
    const resetInsights = useCallback(() => {
        setStats(null);
        setPainPoints([]);
        setPositiveHighlights([]);
        setNegativeHighlights([]);
        setTranscripts([]);
    }, []);

    // Tear down the live connections (audio processors + websockets) and mark
    // the session inactive. Deliberately does NOT clear the insight data — see
    // resetInsights.
    const teardownConnections = useCallback(async () => {
        if (agentProcessorRef.current) {
            try { await removeAudioProcessor(agentProcessorRef.current, false); } catch { /* noop */ }
            agentProcessorRef.current = null;
        }
        if (leadProcessorRef.current) {
            try { await removeAudioProcessor(leadProcessorRef.current, true); } catch { /* noop */ }
            leadProcessorRef.current = null;
        }

        [wsAgentRef, wsLeadRef, wsClientRef].forEach((ref) => {
            if (ref.current?.readyState === WebSocket.OPEN) {
                ref.current.close(1000, "Normal closure");
            }
            ref.current = null;
        });

        isStartingRef.current = false;
        setIsSessionActive(false);
        setConnectionStatus({ audioAgent: false, audioLead: false, client: false });
    }, [removeAudioProcessor]);

    const startSession = useCallback(async (sessionId: string) => {
        if (isStartingRef.current || sessionIdRef.current) return;
        isStartingRef.current = true;

        // Clear the previous call's insights only now that a new call is
        // actually connecting — they persisted on screen until this point.
        resetInsights();

        try {
            const callStartTime = performance.now();

            // 1. Start backend session with platform: "twilio"
            const session = await startSentimentAnalysisSession(sessionId, "twilio");

            if (!session?.websocket_urls) {
                throw new Error("Invalid session response from backend");
            }

            sessionIdRef.current = session.session_id;

            // 2. Open two separate audio WebSockets (agent + lead)
            // URL: /api/realtime/ws/voice-audio/{session_id}?speaker=<name>
            const buildAudioUrl = (name: string) => {
                const url = new URL(`${MEETINGS_AI_SOCKET_BASE_URL}/api/realtime/ws/voice-audio/${session.session_id}`);
                url.searchParams.set("speaker", name);
                return url.toString();
            };

            const wsAgent = await openWebSocket(
                buildAudioUrl(agentNameRef.current || "agent"),
            );
            wsAgentRef.current = wsAgent;
            setConnectionStatus((p) => ({ ...p, audioAgent: true }));
            wsAgent.onerror = () => setConnectionStatus((p) => ({ ...p, audioAgent: false }));
            wsAgent.onclose = () => setConnectionStatus((p) => ({ ...p, audioAgent: false }));

            const wsLead = await openWebSocket(
                buildAudioUrl(leadNameRef.current || "lead"),
            );
            wsLeadRef.current = wsLead;
            setConnectionStatus((p) => ({ ...p, audioLead: true }));
            wsLead.onerror = () => setConnectionStatus((p) => ({ ...p, audioLead: false }));
            wsLead.onclose = () => setConnectionStatus((p) => ({ ...p, audioLead: false }));

            // 3. Open client WebSocket for real-time insights
            const wsClient = await openWebSocket(
                `${MEETINGS_AI_SOCKET_BASE_URL}${session.websocket_urls.client}`,
            );
            wsClientRef.current = wsClient;
            setConnectionStatus((p) => ({ ...p, client: true }));
            wsClient.onerror = () => setConnectionStatus((p) => ({ ...p, client: false }));
            wsClient.onclose = () => setConnectionStatus((p) => ({ ...p, client: false }));
            wsClient.onmessage = (event: MessageEvent) => {
                const data = JSON.parse(event.data) as TSentimentEvent;
                switch (data.type) {
                    case "init":
                        setStats(data.data.stats);
                        setPainPoints(data.data.insights.pain_points);
                        setPositiveHighlights(data.data.insights.positive_highlights);
                        setNegativeHighlights(data.data.insights.negative_highlights);
                        setTranscripts(data.data.transcripts.map((t) => ({ ...t, isFinal: true })));
                        break;
                    case "transcript_final":
                        setStats(data.stats);
                        setTranscripts((prev) => {
                            // Replace partial for this speaker (if any) with the final entry
                            const withoutPartial = prev.filter(
                                (t) => !(t.isFinal === false && t.speaker === data.data.speaker),
                            );
                            return [...withoutPartial, { ...data.data, isFinal: true }];
                        });
                        break;
                    case "transcript_partial":
                        setTranscripts((prev) => {
                            // Upsert: replace existing partial for this speaker
                            const withoutPartial = prev.filter(
                                (t) => !(t.isFinal === false && t.speaker === data.data.speaker),
                            );
                            return [...withoutPartial, { ...data.data, isFinal: false }];
                        });
                        break;
                    case "stats_update":
                        setStats(data.stats);
                        break;
                    case "pain_point":
                        setPainPoints((prev) => [...prev, data.data]);
                        break;
                    case "positive_highlight":
                        setPositiveHighlights((prev) => [...prev, data.data]);
                        break;
                    case "negative_highlight":
                        setNegativeHighlights((prev) => [...prev, data.data]);
                        break;
                }
            };

            // 4. Create and attach AudioProcessors
            const agentProcessor = new TwilioAudioTapProcessor(wsAgent, callStartTime);
            agentProcessorRef.current = agentProcessor;
            await addAudioProcessor(agentProcessor, false); // local mic → agent audio

            const leadProcessor = new TwilioAudioTapProcessor(wsLead, callStartTime);
            leadProcessorRef.current = leadProcessor;
            await addAudioProcessor(leadProcessor, true); // remote stream → lead audio

            setIsSessionActive(true);
        } catch {
            await teardownConnections();
        }
    }, [addAudioProcessor, teardownConnections, resetInsights]);

    const stopSession = useCallback(async () => {
        if (!sessionIdRef.current && !isStartingRef.current) return;
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        // Keep the insights on screen after the call ends — only drop the
        // live connections. They're reset when the next call starts.
        await teardownConnections();
        if (sid) {
            try { await stopSentimentAnalysisSession(sid); } catch { /* noop */ }
        }
    }, [teardownConnections]);

    // Auto-start when call goes in-call
    useEffect(() => {
        if (state === "in-call" && sessionName) {
            startSession(sessionName);
        }
    }, [state, sessionName, startSession]);

    // Auto-stop when call ends
    useEffect(() => {
        if (state === "ended" || state === "error") {
            stopSession();
        }
    }, [state, stopSession]);

    // Cleanup on unmount
    useEffect(() => {
        return () => { teardownConnections(); };
    }, [teardownConnections]);

    return (
        <VoiceSentimentContext.Provider value={{
            isSessionActive,
            connectionStatus,
            stats,
            painPoints,
            positiveHighlights,
            negativeHighlights,
            transcripts,
        }}>
            {children}
        </VoiceSentimentContext.Provider>
    );
}

/* =======================
   Hook
======================= */

export const useVoiceSentimentContext = () => useContext(VoiceSentimentContext);
